import { useEffect, useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, formatUSD, formatDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Mail, Phone, MapPin, Calendar, Award, ExternalLink, Pencil } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function DonorQuickView({ donorId, open, onOpenChange, onEditClick }) {
  const { activeOrgId } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open || !donorId) return;
    setLoading(true);
    api.get(`/organizations/${activeOrgId}/donors/${donorId}/summary`)
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(formatApiError(e)); onOpenChange(false); })
      .finally(() => setLoading(false));
  }, [open, donorId, activeOrgId, onOpenChange]);

  const d = data?.donor;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-lg w-full overflow-y-auto rounded-l-3xl" data-testid="donor-quick-view">
        {loading || !data ? (
          <>
            <SheetHeader className="sr-only"><SheetTitle>Loading donor</SheetTitle></SheetHeader>
            <div className="pt-24 text-center text-muted-foreground text-sm">Loading donor…</div>
          </>
        ) : (
          <>
            <SheetHeader className="pb-4 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center font-serif text-xl">
                  {(d.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase()}
                </div>
                <div className="flex-1 text-left">
                  <SheetTitle className="font-serif text-2xl leading-tight" data-testid="donor-qv-name">{d.name}</SheetTitle>
                  <SheetDescription className="mt-0.5">
                    {d.active === false
                      ? <Badge variant="outline" className="rounded-full bg-muted text-muted-foreground">Inactive</Badge>
                      : <span className="text-xs text-muted-foreground">Active donor</span>}
                  </SheetDescription>
                </div>
              </div>
            </SheetHeader>

            {/* Contact strip */}
            <div className="grid grid-cols-1 gap-2 mt-4 text-sm">
              {d.email && (
                <a href={`mailto:${d.email}`} className="flex items-center gap-2 py-1.5 text-foreground/90 hover:text-primary transition-colors" data-testid="donor-qv-email">
                  <Mail className="w-4 h-4 text-muted-foreground shrink-0" /> {d.email}
                </a>
              )}
              {d.phone && (
                <a href={`tel:${d.phone}`} className="flex items-center gap-2 py-1.5 text-foreground/90 hover:text-primary transition-colors" data-testid="donor-qv-phone">
                  <Phone className="w-4 h-4 text-muted-foreground shrink-0" /> {d.phone}
                </a>
              )}
              {(d.address_line1 || d.city) && (
                <div className="flex items-start gap-2 py-1.5 text-foreground/90" data-testid="donor-qv-address">
                  <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div className="text-sm">
                    {d.address_line1 && <div>{d.address_line1}</div>}
                    {d.address_line2 && <div>{d.address_line2}</div>}
                    <div>{[d.city, d.state, d.postal_code].filter(Boolean).join(", ")}</div>
                  </div>
                </div>
              )}
              {!d.email && !d.phone && !d.address_line1 && !d.city && (
                <div className="text-xs text-muted-foreground italic py-1">No contact info on file.</div>
              )}
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 gap-3 mt-5">
              <div className="rounded-2xl bg-primary/5 p-3">
                <div className="text-[10px] uppercase tracking-wider text-primary/70">Lifetime</div>
                <div className="font-serif text-2xl tabular-nums text-primary mt-1" data-testid="donor-qv-lifetime">{formatUSD(data.lifetime.total)}</div>
                <div className="text-[11px] text-muted-foreground">{data.lifetime.count} gift{data.lifetime.count === 1 ? "" : "s"}</div>
              </div>
              <div className="rounded-2xl bg-secondary/50 p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{data.ytd.year} YTD</div>
                <div className="font-serif text-2xl tabular-nums mt-1" data-testid="donor-qv-ytd">{formatUSD(data.ytd.total)}</div>
                <div className="text-[11px] text-muted-foreground">{data.ytd.count} gift{data.ytd.count === 1 ? "" : "s"}</div>
              </div>
            </div>

            {data.top_fund && (
              <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground" data-testid="donor-qv-top-fund">
                <Award className="w-3.5 h-3.5 text-amber-600" />
                Top fund: <strong className="text-foreground">{data.top_fund.name}</strong> ({formatUSD(data.top_fund.total)})
              </div>
            )}
            {data.first_gift_date && (
              <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                <Calendar className="w-3.5 h-3.5" />
                First gift {formatDate(data.first_gift_date)} · last {formatDate(data.last_gift_date)}
              </div>
            )}

            {/* Recent gifts */}
            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Recent gifts</div>
              {data.recent.length === 0 ? (
                <div className="text-sm text-muted-foreground italic">No gifts recorded yet.</div>
              ) : (
                <div className="rounded-2xl border border-border overflow-hidden" data-testid="donor-qv-recent">
                  <table className="w-full text-xs">
                    <tbody>
                      {data.recent.map((t) => (
                        <tr key={t.id} className="border-b border-border last:border-0">
                          <td className="px-3 py-2 tabular-nums whitespace-nowrap text-muted-foreground">{formatDate(t.date)}</td>
                          <td className="px-3 py-2 text-foreground/80">{t.fund_name || t.category_name || "—"}</td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums text-primary">{formatUSD((t.amount || 0) - (t.fee || 0))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-2 mt-6 pt-4 border-t border-border">
              {onEditClick && (
                <Button
                  variant="outline"
                  className="flex-1 rounded-2xl"
                  onClick={() => { onEditClick(d); onOpenChange(false); }}
                  data-testid="donor-qv-edit"
                >
                  <Pencil className="w-4 h-4 mr-2" /> Edit donor
                </Button>
              )}
              <Button
                className="flex-1 rounded-2xl"
                onClick={() => { onOpenChange(false); navigate(`/donors?highlight=${donorId}`); }}
                data-testid="donor-qv-open"
              >
                Full donor page <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
